import { memo } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { Info } from "lucide-react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { StepHeading } from "@/features/rentals/components/rental-create/wizard/steps/StepHeading";
import type { RentalCreateFormSchema } from "@/features/rentals/components/rental-create/rental-create-schema";

interface StepAdminPeriodProps {
  form: UseFormReturn<RentalCreateFormSchema>;
}

export const StepAdminPeriod = memo(function StepAdminPeriod({
  form,
}: StepAdminPeriodProps) {
  const isRetroactive = useWatch({
    control: form.control,
    name: "isRetroactive",
  });

  return (
    <div className="space-y-6">
      <StepHeading
        title="Período do aluguel"
        description="Retirada e devolução previstas."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="expectedOutAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Data esperada de saída *</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="expectedInAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Data esperada de retorno *</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="flex items-start gap-2 rounded-md border border-input bg-muted/40 p-3 text-xs text-muted-foreground">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <p>
          As datas definem o período usado para checar a disponibilidade das
          peças na próxima etapa.
        </p>
      </div>

      <FormField
        control={form.control}
        name="isRetroactive"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={field.value ?? false}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    // Ao desmarcar, limpa as datas efetivas para não
                    // persisti-las por engano.
                    if (!e.target.checked) {
                      form.setValue("outAt", "");
                      form.setValue("inAt", "");
                    }
                  }}
                />
                <span>
                  Aluguel retroativo
                  <span className="block text-muted-foreground">
                    Marque para registrar as datas em que a peça de fato saiu e
                    retornou.
                  </span>
                </span>
              </label>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {isRetroactive ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="outAt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data efetiva de saída *</FormLabel>
                <FormControl>
                  <Input type="date" {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="inAt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data efetiva de retorno *</FormLabel>
                <FormControl>
                  <Input type="date" {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      ) : null}
    </div>
  );
});
